import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PathKind } from '@platform/dao';
import { useCreatePathNodeMutation, useDeletePathNodeMutation, useMeQuery, useUpdatePathNodeMutation } from '@platform/state/api';
import { useFailToast } from '../../system/useFailToast';
import type { IPathEdit } from './IPathEdit';
import type { IPathEditor } from './IPathEditor';

/**
 * Зміни шляхів (прототип v4 areas/iterations): форма під рядком — новий дочірній вузол (POST) чи перейменування / дати (PATCH), Enter
 * зберігає, Escape скасовує; видалення гілки після підтвердження (DELETE: записи переходять до батьківського вузла); колір напряму
 * (PATCH color, "" — успадкувати). Змінює той, хто має можливість customize.
 */
export function usePathEditor(ws: string, kind: PathKind): IPathEditor {
  const { t } = useTranslation();
  const { data: me } = useMeQuery();
  const [create] = useCreatePathNodeMutation();
  const [update] = useUpdatePathNodeMutation();
  const [del] = useDeletePathNodeMutation();
  const fail = useFailToast();
  const [edit, setEdit] = useState<IPathEdit | null>(null);
  const [palette, setPalette] = useState<string | null>(null);
  const dates = (e: IPathEdit) => (kind === 'iterations' ? { startDate: e.start || null, endDate: e.end || null } : {});
  const save = () => {
    if (!edit) return;
    const name = edit.name.trim();
    if (!name) return;
    const request = edit.mode === 'add'
      ? create({ ws, kind, body: { parentId: edit.nodeId, name, ...dates(edit) } })
      : update({ ws, kind, nodeId: edit.nodeId, body: { name, ...dates(edit) } });
    request.unwrap().then(() => setEdit(null), fail);
  };
  return {
    edit, palette, canEdit: !!me?.workspaces.find((w) => w.id === ws)?.myCapabilities.includes('customize'),
    startAdd: (node) => { setPalette(null); setEdit({ mode: 'add', nodeId: node.id, name: '', start: '', end: '' }); },
    startEdit: (node) => { setPalette(null); setEdit({ mode: 'edit', nodeId: node.id, name: node.name, start: node.startDate ?? '', end: node.endDate ?? '' }); },
    change: (patch) => setEdit((e) => (e ? { ...e, ...patch } : e)),
    save,
    cancel: () => setEdit(null),
    remove: (node, records, parent) => {
      const text = t('sysCust.paths.deleteConfirm', { name: node.name }) + (records ? ` ${t('sysCust.paths.deleteMoves', { count: records, parent })}` : '');
      if (window.confirm(text)) del({ ws, kind, nodeId: node.id }).unwrap().catch(fail);
    },
    setColor: (node, color) => { update({ ws, kind, nodeId: node.id, body: { color } }).unwrap().then(() => setPalette(null), fail); },
    togglePalette: (node) => { setEdit(null); setPalette((p) => (p === node.id ? null : node.id)); },
  };
}
